import type { ElementKind, LayoutKind } from './types'
import type { StaffKey } from '../lib/staffText'

/**
 * Which of the inspector's style settings mean anything for a kind of element.
 *
 * A setting that changes nothing is worse than no setting: the designer moves it,
 * sees nothing happen, and concludes the editor is broken. So each kind is offered
 * only what its renderers read, on a screen (render.tsx) and on paper
 * (internal/slip/designed.go). Position, size and depth are always offered.
 */
export interface StyleControls {
  align: boolean
  weight: boolean
  colour: boolean
  background: boolean
  size: boolean
}

const ALL: StyleControls = { align: true, weight: true, colour: true, background: true, size: true }
const NONE: StyleControls = { align: false, weight: false, colour: false, background: false, size: false }

/** A Record rather than a switch, so a new kind is a type error here until it is decided. */
const CONTROLS: Record<ElementKind, StyleControls> = {
  calls: ALL,
  queues: ALL,
  waitingTotal: ALL,
  clock: ALL,
  branchName: ALL,
  languages: ALL,
  services: ALL,
  ticketNumber: ALL,
  ticketFacts: ALL,
  ticketPosition: ALL,
  ticketWait: ALL,
  dateTime: ALL,
  validUntil: ALL,
  text: ALL,
  button: ALL,
  // MediaPlayer draws at 100% × 100% with objectFit contain, so the box's
  // background shows around a letterboxed film. Nothing reads the rest.
  media: { ...NONE, background: true },
  // render.tsx uses align as alignSelf; scale sizes only the editor's placeholder,
  // and the agent's drawPicture reads no style.
  image: { ...NONE, align: true, background: true },
  // render.tsx colours the line; the agent's drawRule reads no style.
  rule: { ...NONE, colour: true, background: true },
  // QRBlock sizes itself from the element's width; the agent's drawQR reads no style.
  qr: { ...NONE, background: true },
  // Ticker.tsx: the box's colour, background and weight, and its font from the scale; it is not aligned.
  ticker: { ...NONE, colour: true, background: true, size: true, weight: true },
  // render.tsx's BrandLogo: Qmeter's wordmark takes the colour, and both align; paper prints the picture centred.
  logo: { ...NONE, align: true, colour: true, background: true },
  // Drawn by FittedText like the branch name.
  deskName: ALL,
  serviceName: ALL,
  deskStatus: ALL,
  deskNumber: ALL,
  // The pill's colours are its state's; only its text is sized and weighted.
  statusPill: { ...NONE, align: true, weight: true, size: true },
  clerkName: ALL,
  // The inspector's own Fill, Border, Corners and Shadow; render.tsx's boxStyle draws them.
  panel: NONE,
}

/** Which of the inspector's style settings change what an element of this kind looks like,
 *  on a screen (render.tsx) and on paper (internal/slip/designed.go). */
export function styleControls(kind: ElementKind): StyleControls {
  // A kind this build no longer knows (a stored design's) gets no settings rather than a crash.
  return CONTROLS[kind] ?? NONE
}

/** A printed ticket: no theme, and only what a thermal head prints. */
export function paperOnly(kind: LayoutKind): boolean {
  return kind === 'ticket'
}

const SCREEN_COLOURS: Array<{ token: string; label: StaffKey }> = [
  { token: '--ink-strong', label: 'designs.insp.inkStrong' },
  { token: '--ink-body', label: 'designs.insp.inkBody' },
  { token: '--ink-muted', label: 'designs.insp.inkMuted' },
  // --accent-solid, not --accent: the design system names the brand colour that
  // way, and a token that does not exist renders as nothing at all.
  { token: '--accent-solid', label: 'designs.insp.accent' },
  { token: '--success', label: 'designs.insp.success' },
  { token: '--danger', label: 'designs.insp.danger' },
  { token: '--bg-surface', label: 'designs.insp.surface' },
  { token: '--bg-muted', label: 'designs.insp.mutedSurface' },
]

const PAPER_COLOURS: Array<{ token: string; label: StaffKey }> = [
  { token: '#000000', label: 'designs.insp.black' },
  { token: '#ffffff', label: 'designs.insp.white' },
]

/** The swatches offered for an element's colours (the empty "none" is the Swatches' own). */
export function colourChoices(kind: LayoutKind): Array<{ token: string; label: StaffKey }> {
  return paperOnly(kind) ? PAPER_COLOURS : SCREEN_COLOURS
}

/** Whether the Theme switch is offered: never on paper, except to undo a ticket left dark. */
export function themeOffered(kind: LayoutKind, theme: string | undefined): boolean {
  return !paperOnly(kind) || theme === 'dark'
}

/** A value set before paper allowed only black and white, shown as the current swatch. */
export function strayColour(value: string, choices: ReadonlyArray<{ value: string }>): boolean {
  return value !== '' && !choices.some((c) => c.value === value)
}
